import { WidgetType } from '~/types/widget';
import type { WidgetFormValues, WidgetTypeValue } from '~/types/widget';

export const INCLUDED_COPY_ERROR = "Use 'Included', not 'Free'. The buyer pays for protection in shipping.";

/** The checkout Included block's merchant-editable copy. */
export const INCLUDED_CHECKOUT_COPY_FIELDS = [
  'includedHeading',
  'includedDescription',
  'includedCoveredLinkLabel',
  'includedPerRateLabel',
  'includedSummaryLine',
] as const satisfies readonly (keyof WidgetFormValues)[];

type CopyNode = { attributes?: Record<string, unknown>; children?: CopyNode[] };

// "Free" as a word, not inside compounds like "worry-free" or "carefree".
const FORBIDDEN_COPY = /(?<![\w-])free(?![\w-])|%\s*off/i;

// URLs and file names aren't buyer-facing copy, so an image like `.../free.png` doesn't block Publish.
const URL_LIKE = /^(https?:|data:|\/)|\.(png|jpe?g|gif|svg|webp)$/i;

export const isForbiddenIncludedCopy = (text: unknown): boolean =>
  typeof text === 'string' && !URL_LIKE.test(text) && FORBIDDEN_COPY.test(text);

// Every string attribute counts, since modal titles and steps carry buyer-facing copy too.
const treeHasForbiddenCopy = (nodes: CopyNode[]): boolean =>
  nodes.some(
    node =>
      Object.values(node.attributes ?? {}).some(isForbiddenIncludedCopy) || treeHasForbiddenCopy(node.children ?? []),
  );

/** True when an Included checkout widget's fields, or a cart widget's element copy, read as a discount. */
export function hasForbiddenIncludedCopy(
  widgetType: WidgetTypeValue | undefined,
  formValues: Partial<WidgetFormValues>,
  tree: CopyNode[] = [],
): boolean {
  if (widgetType !== WidgetType.CHECKOUT) return treeHasForbiddenCopy(tree);

  return INCLUDED_CHECKOUT_COPY_FIELDS.some(field => isForbiddenIncludedCopy(formValues[field]));
}
